// DynamicActionButtons — Phase 6B replacement for the hardcoded
// SkipJobActionButtons / SkipJobWorkflowModal pair.
//
// Reads the per-job action list from the server via
// useAvailableActions and renders one button per action. Click
// behaviour is dispatched by action_key:
//   - 'collection'   → ProcessCollectionModal
//   - 'exchange'     → ProcessExchangeModal
//   - 'tip_return'   → ProcessTipReturnModal
//   - 'confirm'      → ConfirmActionModal (generic)
//   - unknown        → disabled button + console.warn
//
// Empty actions list → renders nothing (terminal stage). Loading →
// two ghost skeleton rectangles.

import { useState } from 'react';

import { Button } from '../../../../components/ui/Button/Button';
import { useAvailableActions } from '../../hooks/useSkipJobs';
import { ConfirmActionModal } from './ConfirmActionModal';
import { MarkDeliveredModal } from './MarkDeliveredModal';
import { ProcessCollectionModal } from './ProcessCollectionModal';
import { ProcessExchangeModal } from './ProcessExchangeModal';
import { ProcessTipReturnModal } from './ProcessTipReturnModal';
import { getActionKeyConfig } from './actionKeyEndpoints';

import styles from './DynamicActionButtons.module.css';

export interface DynamicActionButtonsProps {
  jobEncId: string;
  jobNumber: string;
  customerName: string;
  /** Bug 3 — propagated to ProcessExchangeModal so the replacement
   *  inventory dropdown filters by the parent job's chosen size.
   *  Null when the job has no size set (legacy data). */
  skipSizeEncId: string | null;
  onActionComplete?: () => void;
}

export function DynamicActionButtons({
  jobEncId,
  jobNumber,
  customerName,
  skipSizeEncId,
  onActionComplete,
}: DynamicActionButtonsProps): JSX.Element | null {
  const { data, isLoading } = useAvailableActions(jobEncId);

  // The action_key currently mounting a modal; null = no modal.
  const [openActionKey, setOpenActionKey] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className={styles.row}>
        <div className={styles.skeleton} aria-hidden />
        <div className={styles.skeleton} aria-hidden />
      </div>
    );
  }

  const actions = (data?.actions ?? [])
    .slice()
    .sort((a, b) => {
      if (a.is_primary !== b.is_primary) return a.is_primary ? -1 : 1;
      return a.sort_order - b.sort_order;
    });

  if (actions.length === 0) {
    return null;
  }

  function close() {
    setOpenActionKey(null);
    onActionComplete?.();
  }

  // Look up the action object for the open modal so it can be passed
  // to ConfirmActionModal.
  const openAction =
    openActionKey === null
      ? null
      : actions.find((a) => a.action_key === openActionKey) ?? null;
  const openConfig = openActionKey === null ? null : getActionKeyConfig(openActionKey);

  return (
    <div className={styles.row}>
      {actions.map((action) => {
        const config = getActionKeyConfig(action.action_key);
        if (config === null) {
          // eslint-disable-next-line no-console
          console.warn(
            `DynamicActionButtons: no actionKeyEndpoints config for "${action.action_key}". Rendering disabled button.`,
          );
          return (
            <Button
              key={action.action_key}
              variant="ghost"
              disabled
              title={`No client mapping for ${action.action_key}`}
            >
              {action.button_label}
            </Button>
          );
        }
        return (
          <Button
            key={action.action_key}
            variant={action.is_primary ? 'primary' : 'secondary'}
            onClick={() => setOpenActionKey(action.action_key)}
          >
            {action.button_label}
          </Button>
        );
      })}

      {/* One modal at a time. ConfirmActionModal needs the full DTO,
          ProcessXModal subclasses just need the basic job context. */}
      {openAction !== null && openConfig?.modalType === 'confirm' ? (
        <ConfirmActionModal
          isOpen
          jobEncId={jobEncId}
          action={openAction}
          onClose={close}
        />
      ) : null}
      {openConfig?.modalType === 'collection' ? (
        <ProcessCollectionModal
          isOpen
          jobEncId={jobEncId}
          jobNumber={jobNumber}
          customerName={customerName}
          onClose={close}
        />
      ) : null}
      {openConfig?.modalType === 'exchange' ? (
        <ProcessExchangeModal
          isOpen
          jobEncId={jobEncId}
          jobNumber={jobNumber}
          customerName={customerName}
          skipSizeEncId={skipSizeEncId}
          onClose={close}
        />
      ) : null}
      {openConfig?.modalType === 'tip_return' ? (
        <ProcessTipReturnModal
          isOpen
          jobEncId={jobEncId}
          jobNumber={jobNumber}
          customerName={customerName}
          onClose={close}
        />
      ) : null}
      {openConfig?.modalType === 'mark_delivered' ? (
        <MarkDeliveredModal
          isOpen
          jobEncId={jobEncId}
          jobNumber={jobNumber}
          customerName={customerName}
          onClose={close}
        />
      ) : null}
    </div>
  );
}
